import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table, Button, Alert, Spinner, Badge,
  Accordion, Form, Modal
} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { qscAPI } from '../utils/api';
import QscScoreBadge from '../components/QscScoreBadge';
import QscGradeBadge from '../components/QscGradeBadge';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
const imgUrl = (filePath) => `${API_BASE}/${filePath.replace(/^\//, '')}`;

const QscDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperuser } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const loadedIdRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [improvDesc, setImprovDesc] = useState('');
  const [improvFiles, setImprovFiles] = useState([]);
  const [submittingImprov, setSubmittingImprov] = useState(false);
  const [improvError, setImprovError] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewImp, setReviewImp] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await qscAPI.deleteAssessment(id);
      navigate('/qsc');
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบ');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const openReviewModal = (imp) => {
    setReviewImp(imp);
    setReviewComment(imp.reviewerComment || '');
    setReviewError('');
    setShowReviewModal(true);
  };

  const handleReview = async (status) => {
    setSubmittingReview(true);
    setReviewError('');
    try {
      await qscAPI.reviewImprovement(reviewImp.id, { status, reviewerComment: reviewComment });
      setShowReviewModal(false);
      await loadAssessment();
    } catch (err) {
      setReviewError(err.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setSubmittingReview(false);
    }
  };

  useEffect(() => {
    if (loadedIdRef.current !== id) {
      loadedIdRef.current = id;
      loadAssessment();
    }
  }, [id]);

  const loadAssessment = async () => {
    try {
      setLoading(true);
      const res = await qscAPI.getAssessment(id);
      setAssessment(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const openImprovementModal = (item) => {
    setModalItem(item);
    setImprovDesc('');
    setImprovFiles([]);
    setImprovError('');
    setShowModal(true);
  };

  const handleSubmitImprovement = async () => {
    if (!improvDesc.trim()) { setImprovError('กรุณาระบุรายละเอียดการแก้ไข'); return; }
    setSubmittingImprov(true);
    setImprovError('');
    try {
      const impRes = await qscAPI.submitImprovement({
        assessmentId: parseInt(id),
        itemId: modalItem.id,
        description: improvDesc
      });
      const impId = impRes.data.data.id;
      if (improvFiles.length > 0) {
        const formData = new FormData();
        for (const file of improvFiles) formData.append('photos', file);
        await qscAPI.uploadImprovementPhotos(impId, formData);
      }
      setShowModal(false);
      await loadAssessment();
    } catch (err) {
      setImprovError(err.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setSubmittingImprov(false);
    }
  };

  if (loading) return (
    <Container fluid className="main-content">
      <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
    </Container>
  );
  if (error) return (
    <Container fluid className="main-content"><Alert variant="danger">{error}</Alert></Container>
  );
  if (!assessment) return null;

  // Sort category scores by category sortOrder
  const sortedScores = [...(assessment.categoryScores || [])].sort(
    (a, b) => (a.category?.sortOrder || 0) - (b.category?.sortOrder || 0)
  );

  // Group assessment items by category id
  const groupedItems = {};
  for (const ai of (assessment.items || [])) {
    const catId = ai.item?.category?.id || ai.item?.categoryId;
    if (!catId) continue;
    if (!groupedItems[catId]) groupedItems[catId] = [];
    groupedItems[catId].push(ai);
  }

  // Map improvements by itemId
  const improvByItemId = {};
  for (const imp of (assessment.improvements || [])) {
    improvByItemId[imp.itemId] = imp;
  }

  // Map assessment item evidence photos by itemId
  const evidenceByItemId = {};
  for (const photo of (assessment.assessmentItemPhotos || [])) {
    if (!evidenceByItemId[photo.itemId]) evidenceByItemId[photo.itemId] = [];
    evidenceByItemId[photo.itemId].push(photo);
  }

  const failedItems = (assessment.items || []).filter(ai => parseFloat(ai.receivedScore) === 0);
  const disabledIds = (assessment.disabledCategoryIds || []).map(Number);

  const approvedCount = (assessment.improvements || []).filter(i => i.status === 'approved').length;
  const allFixed = failedItems.length > 0 && approvedCount >= failedItems.length;

  const improvStatusBadge = (status) => {
    if (status === 'approved') return <Badge bg="success">อนุมัติแล้ว</Badge>;
    if (status === 'rejected') return <Badge bg="danger">ถูกปฏิเสธ</Badge>;
    return <Badge bg="warning" text="dark">รอการตรวจสอบ</Badge>;
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-3 align-items-center">
        <Col>
          <h2><i className="bi bi-clipboard-check me-2"></i>ผลการประเมิน QSC</h2>
          <div className="text-muted">
            สาขา: <strong>{assessment.branch?.fullName || assessment.branch?.username}</strong>
            {' · '}วันที่ตรวจ: <strong>{assessment.visitDate}</strong>
            {' · '}ผู้ตรวจ: <strong>{assessment.assessor?.fullName || assessment.assessor?.username}</strong>
          </div>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          {isSuperuser() && (
            <>
              <Button as={Link} to="/qsc/improvements" variant="outline-primary" size="sm">
                <i className="bi bi-images me-1"></i>รีวิวการแก้ไข
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => setShowDeleteModal(true)}>
                <i className="bi bi-trash me-1"></i>ลบแบบประเมิน
              </Button>
            </>
          )}
          <Button as={Link} to="/qsc" variant="outline-secondary" size="sm">
            <i className="bi bi-arrow-left me-1"></i>กลับ
          </Button>
        </Col>
      </Row>

      {/* Dynamic category score cards */}
      <Row className="mb-4">
        {sortedScores.filter(cs => !disabledIds.includes(cs.categoryId)).map(cs => (
          <Col key={cs.id} xs={6} md={3} className="mb-3">
            <Card className="text-center h-100">
              <Card.Body>
                <div className="text-muted small mb-1">{cs.category?.name}</div>
                <div className="fs-3 fw-bold">{parseFloat(cs.score).toFixed(1)}%</div>
                <QscScoreBadge score={cs.score} />
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Overall result */}
      <Card className="mb-4">
        <Card.Body className="text-center py-4">
          <Row className="align-items-center justify-content-center">
            <Col xs="auto">
              <div className="text-muted mb-1">คะแนนรวม</div>
              <div className="display-4 fw-bold">
                {assessment.overallScore != null ? `${parseFloat(assessment.overallScore).toFixed(1)}%` : '-'}
              </div>
            </Col>
            <Col xs="auto">
              {assessment.grade && <QscGradeBadge grade={assessment.grade} />}
              <div className="mt-2">
                {assessment.isPass != null && (
                  <Badge bg={assessment.isPass ? 'success' : 'danger'} style={{ fontSize: '1rem' }}>
                    {assessment.isPass ? 'ผ่าน' : 'ไม่ผ่าน'}
                  </Badge>
                )}
              </div>
            </Col>
          </Row>
          {assessment.notes && (
            <div className="mt-3 text-muted small">
              <strong>หมายเหตุผู้ตรวจ:</strong> {assessment.notes}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Improvement status banner */}
      {failedItems.length > 0 && (
        <Alert variant={allFixed ? 'success' : 'warning'} className="mb-4 d-flex align-items-center gap-2">
          {allFixed ? (
            <><i className="bi bi-check-circle-fill fs-5"></i>
            <span>รายการที่ไม่ผ่านทั้งหมด <strong>{failedItems.length} รายการ</strong> ได้รับการแก้ไขและอนุมัติแล้ว</span></>
          ) : (
            <><i className="bi bi-exclamation-circle-fill fs-5"></i>
            <span>แก้ไขแล้ว <strong>{approvedCount}</strong> จาก <strong>{failedItems.length}</strong> รายการที่ไม่ผ่าน</span></>
          )}
        </Alert>
      )}

     {/* HQ: all failed items + improvement status */}
      {isSuperuser() && failedItems.length > 0 && (
        <Card className="mb-4">
          <Card.Header className="fw-bold">
            <i className="bi bi-images me-2"></i>
            รายการที่ต้องแก้ไข ({failedItems.length} รายการ
            {approvedCount > 0 && ` · อนุมัติแล้ว ${approvedCount}`})
          </Card.Header>
          <Card.Body>
            <Row>
              {failedItems.map(ai => {
                const imp = improvByItemId[ai.itemId];
                const evidencePhotos = evidenceByItemId[ai.itemId] || [];
                const afterPhotos = [...(imp?.photos || [])].sort(
                  (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                const beforePhoto = evidencePhotos[0];
                const afterPhoto = afterPhotos[0];
                return (
                  <Col key={ai.id} xs={12} md={6} className="mb-4">
                    <Card className="h-100 shadow-sm border">
                      {/* Before / After image strip */}
                      <div className="d-flex" style={{ borderBottom: '1px solid #dee2e6' }}>
                        {/* Before */}
                        <div className="flex-fill text-center" style={{ width: '50%' }}>
                          <div className="text-muted small fw-semibold py-1 border-bottom"
                            style={{ background: '#f8f9fa', letterSpacing: 1 }}>ก่อนแก้</div>
                          {beforePhoto ? (
                            <a href={`${imgUrl(beforePhoto.filePath)}`} target="_blank" rel="noopener noreferrer">
                              <img src={`${imgUrl(beforePhoto.filePath)}`} alt="before"
                                style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                            </a>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center text-muted"
                              style={{ height: 200, background: '#f0faf0', fontSize: '0.8rem' }}>
                              ไม่มีรูปหลักฐาน
                            </div>
                          )}
                          {evidencePhotos.length > 1 && (
                            <div className="d-flex gap-1 p-1 flex-wrap justify-content-center"
                              style={{ background: '#f8f9fa' }}>
                              {evidencePhotos.slice(1).map(p => (
                                <a key={p.id} href={`${imgUrl(p.filePath)}`} target="_blank" rel="noopener noreferrer">
                                  <img src={`${imgUrl(p.filePath)}`} alt="before extra"
                                    style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 3 }} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Divider */}
                        <div style={{ width: 1, background: '#dee2e6' }} />
                        {/* After */}
                        <div className="flex-fill text-center" style={{ width: '50%' }}>
                          <div className="text-muted small fw-semibold py-1 border-bottom"
                            style={{ background: '#f8f9fa', letterSpacing: 1 }}>หลังแก้</div>
                          {afterPhoto ? (
                            <a href={`${imgUrl(afterPhoto.filePath)}`} target="_blank" rel="noopener noreferrer">
                              <img src={`${imgUrl(afterPhoto.filePath)}`} alt="after"
                                style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                            </a>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center text-muted"
                              style={{ height: 200, background: '#f0faf0', fontSize: '0.8rem' }}>
                              {imp ? 'ไม่มีรูปการแก้ไข' : 'รอสาขาส่งการแก้ไข'}
                            </div>
                          )}
                          {afterPhotos.length > 1 && (
                            <div className="d-flex gap-1 p-1 flex-wrap justify-content-center"
                              style={{ background: '#f8f9fa' }}>
                              {afterPhotos.slice(1).map(p => (
                                <a key={p.id} href={`${imgUrl(p.filePath)}`} target="_blank" rel="noopener noreferrer">
                                  <img src={`${imgUrl(p.filePath)}`} alt="after extra"
                                    style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 3 }} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card body */}
                      <Card.Body>
                        <div className="mb-2">
                          <Badge bg="dark" className="me-1" style={{ fontSize: '0.7rem' }}>สาเหตุ</Badge>
                          <span className="small text-muted">{ai.item?.itemCode}</span>
                        </div>
                        <div className="mb-2">
                          <Badge bg="secondary" className="me-1" style={{ fontSize: '0.7rem' }}>หัวข้อ</Badge>
                          <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                            {ai.item?.itemCode} {ai.item?.description}
                          </span>
                        </div>
                        {ai.remarks && (
                          <div className="mb-2 p-2 rounded small" style={{ background: '#fff8e1', borderLeft: '3px solid #ffc107' }}>
                            <span className="fw-semibold text-warning-emphasis">หมายเหตุผู้ตรวจ: </span>
                            {ai.remarks}
                          </div>
                        )}

                        <div className="mb-1 small">
                          <span className="text-muted">ผู้แก้ไข: </span>
                          {imp?.submitter?.fullName || imp?.submitter?.username || <span className="text-muted">–</span>}
                        </div>
                        <div className="mb-3 small">
                          <span className="text-muted">รายละเอียด: </span>
                          {imp?.description || <span className="text-muted">–</span>}
                        </div>

                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div>
                            {imp ? improvStatusBadge(imp.status) : <Badge bg="secondary">ยังไม่ส่ง</Badge>}
                            {imp?.reviewerComment && (
                              <div className="text-muted small mt-1">ความเห็น: {imp.reviewerComment}</div>
                            )}
                          </div>
                          {imp && (
                            <Button size="sm"
                              variant={imp.status === 'pending' ? 'primary' : 'outline-secondary'}
                              onClick={() => openReviewModal(imp)}>
                              <i className={`bi ${imp.status === 'pending' ? 'bi-check2-circle' : 'bi-pencil'} me-1`}></i>
                              {imp.status === 'pending' ? 'ตรวจสอบ' : 'แก้ไขผล'}
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}


      {/* Branch: failed items + improvement upload */}
      {!isSuperuser() && failedItems.length > 0 && (
        <Card className="mb-4">
          <Card.Header className="fw-bold bg-danger text-white">
            <i className="bi bi-exclamation-triangle me-2"></i>
            รายการที่ต้องแก้ไข ({failedItems.length} รายการ)
          </Card.Header>
          <Card.Body>
            <Row>
              {failedItems.map(ai => {
                const imp = improvByItemId[ai.itemId];
                const evidencePhotos = evidenceByItemId[ai.itemId] || [];
                const afterPhotos = [...(imp?.photos || [])].sort(
                  (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                const beforePhoto = evidencePhotos[0];
                const afterPhoto = afterPhotos[0];
                return (
                  <Col key={ai.id} xs={12} md={6} className="mb-4">
                    <Card className="h-100 shadow-sm border">
                      {/* Before / After image strip */}
                      <div className="d-flex" style={{ borderBottom: '1px solid #dee2e6' }}>
                        {/* Before */}
                        <div className="flex-fill text-center" style={{ width: '50%' }}>
                          <div className="text-muted small fw-semibold py-1 border-bottom"
                            style={{ background: '#f8f9fa', letterSpacing: 1 }}>ก่อนแก้</div>
                          {beforePhoto ? (
                            <a href={`${imgUrl(beforePhoto.filePath)}`} target="_blank" rel="noopener noreferrer">
                              <img src={`${imgUrl(beforePhoto.filePath)}`} alt="before"
                                style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                            </a>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center text-muted"
                              style={{ height: 200, background: '#f0faf0', fontSize: '0.8rem' }}>
                              ไม่มีรูปหลักฐาน
                            </div>
                          )}
                          {evidencePhotos.length > 1 && (
                            <div className="d-flex gap-1 p-1 flex-wrap justify-content-center"
                              style={{ background: '#f8f9fa' }}>
                              {evidencePhotos.slice(1).map(p => (
                                <a key={p.id} href={`${imgUrl(p.filePath)}`} target="_blank" rel="noopener noreferrer">
                                  <img src={`${imgUrl(p.filePath)}`} alt="before extra"
                                    style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 3 }} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Divider */}
                        <div style={{ width: 1, background: '#dee2e6' }} />
                        {/* After */}
                        <div className="flex-fill text-center" style={{ width: '50%' }}>
                          <div className="text-muted small fw-semibold py-1 border-bottom"
                            style={{ background: '#f8f9fa', letterSpacing: 1 }}>หลังแก้</div>
                          {afterPhoto ? (
                            <a href={`${imgUrl(afterPhoto.filePath)}`} target="_blank" rel="noopener noreferrer">
                              <img src={`${imgUrl(afterPhoto.filePath)}`} alt="after"
                                style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                            </a>
                          ) : (
                            <div className="d-flex align-items-center justify-content-center text-muted"
                              style={{ height: 200, background: '#f0faf0', fontSize: '0.8rem' }}>
                              ภาพหลังแก้ไข
                            </div>
                          )}
                          {afterPhotos.length > 1 && (
                            <div className="d-flex gap-1 p-1 flex-wrap justify-content-center"
                              style={{ background: '#f8f9fa' }}>
                              {afterPhotos.slice(1).map(p => (
                                <a key={p.id} href={`${imgUrl(p.filePath)}`} target="_blank" rel="noopener noreferrer">
                                  <img src={`${imgUrl(p.filePath)}`} alt="after extra"
                                    style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 3 }} />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Card body: item info + status + action */}
                      <Card.Body>
                        <div className="mb-2">
                          <Badge bg="dark" className="me-1" style={{ fontSize: '0.7rem' }}>สาเหตุ</Badge>
                          <span className="small text-muted">{ai.item?.itemCode}</span>
                        </div>
                        <div className="mb-2">
                          <Badge bg="secondary" className="me-1" style={{ fontSize: '0.7rem' }}>หัวข้อ</Badge>
                          <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                            {ai.item?.itemCode} {ai.item?.description}
                          </span>
                        </div>
                        {ai.remarks && (
                          <div className="mb-3 p-2 rounded small" style={{ background: '#fff8e1', borderLeft: '3px solid #ffc107' }}>
                            <span className="fw-semibold text-warning-emphasis">หมายเหตุผู้ตรวจ: </span>
                            {ai.remarks}
                          </div>
                        )}

                        <div className="mb-1 small">
                          <span className="text-muted">ผู้แก้ไข: </span>
                          {imp?.submitter?.fullName || imp?.submitter?.username || (
                            <span className="text-muted">–</span>
                          )}
                        </div>
                        <div className="mb-3 small">
                          <span className="text-muted">รายละเอียด: </span>
                          {imp?.description || <span className="text-muted">–</span>}
                        </div>

                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div>
                            {imp ? improvStatusBadge(imp.status) : <Badge bg="secondary">ยังไม่ส่ง</Badge>}
                            {imp?.reviewerComment && (
                              <div className="text-muted small mt-1">ความเห็น: {imp.reviewerComment}</div>
                            )}
                          </div>
                          {(!imp || imp.status === 'rejected') && (
                            <Button size="sm" variant="primary" onClick={() => openImprovementModal(ai.item)}>
                              <i className="bi bi-upload me-1"></i>ส่งการแก้ไข
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

        {/* Full checklist grouped by category */}
      <Card className="mb-4">
        <Card.Header className="fw-bold">
          <i className="bi bi-list-check me-2"></i>ผลรายการตรวจทั้งหมด
        </Card.Header>
        <Card.Body className="p-0">
          <Accordion defaultActiveKey={null}>
            {sortedScores.filter(cs => !disabledIds.includes(cs.categoryId)).map(cs => {
              const catItems = groupedItems[cs.categoryId] || [];
              const failCount = catItems.filter(i => parseFloat(i.receivedScore) === 0).length;
              return (
                <Accordion.Item eventKey={String(cs.categoryId)} key={cs.categoryId}>
                  <Accordion.Header>
                    <span className="fw-bold me-2">{cs.category?.name}</span>
                    <Badge bg="secondary" className="me-1">{catItems.length} รายการ</Badge>
                    {failCount > 0 && <Badge bg="danger">ไม่ผ่าน {failCount}</Badge>}
                  </Accordion.Header>
                  <Accordion.Body className="p-0">
                    <Table hover size="sm" className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>รหัส</th>
                          <th>รายการ</th>
                          <th className="text-center">ผล</th>
                          <th>หมายเหตุ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catItems.map(ai => {
                          const isFail = parseFloat(ai.receivedScore) === 0;
                          return (
                            <tr key={ai.id} className={isFail ? 'table-danger' : ''}>
                              <td>
                                <code>{ai.item?.itemCode}</code>
                                {parseFloat(ai.item?.criticalPct) > 0 && (
                                  <Badge bg="danger" className="ms-1" style={{ fontSize: '0.65rem' }}>
                                    -{ai.item?.criticalPct}%
                                  </Badge>
                                )}
                              </td>
                              <td style={{ fontSize: '0.875rem' }}>{ai.item?.description}</td>
                              <td className="text-center">
                                {isFail
                                  ? <Badge bg="danger"><i className="bi bi-x-lg"></i> ไม่ผ่าน</Badge>
                                  : <Badge bg="success"><i className="bi bi-check-lg"></i> ผ่าน</Badge>}
                              </td>
                              <td style={{ fontSize: '0.875rem' }}>{ai.remarks || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Card.Body>
      </Card>

     
      {/* HQ: review modal */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-check2-circle me-2"></i>
            ตรวจสอบการแก้ไข: <code>{reviewImp?.item?.itemCode}</code>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reviewError && <Alert variant="danger">{reviewError}</Alert>}
          <div className="mb-3 small text-muted">{reviewImp?.item?.description}</div>

          {/* Before / After */}
          <Row className="mb-3 g-3">
            <Col xs={6}>
              <div className="text-center fw-semibold small mb-2 text-muted">ก่อนแก้ (หลักฐาน HQ)</div>
              {(() => {
                const photos = evidenceByItemId[reviewImp?.itemId] || [];
                return photos.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 justify-content-center">
                    {photos.map(p => (
                      <a key={p.id} href={`${imgUrl(p.filePath)}`} target="_blank" rel="noopener noreferrer">
                        <img src={`${imgUrl(p.filePath)}`} alt="before"
                          style={{ width: '100%', maxWidth: 220, height: 180, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center rounded text-muted small"
                    style={{ height: 180, background: '#f0faf0', border: '1px dashed #ccc' }}>
                    ไม่มีรูปหลักฐาน
                  </div>
                );
              })()}
            </Col>
            <Col xs={6}>
              <div className="text-center fw-semibold small mb-2 text-muted">หลังแก้ (ส่งโดยสาขา)</div>
              {reviewImp?.photos?.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {reviewImp.photos.map(p => (
                    <a key={p.id} href={`${imgUrl(p.filePath)}`} target="_blank" rel="noopener noreferrer">
                      <img src={`${imgUrl(p.filePath)}`} alt="after"
                        style={{ width: '100%', maxWidth: 220, height: 180, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-center rounded text-muted small"
                  style={{ height: 180, background: '#f0faf0', border: '1px dashed #ccc' }}>
                  ไม่มีรูปการแก้ไข
                </div>
              )}
            </Col>
          </Row>

          <div className="mb-3 small">
            <strong>รายละเอียดการแก้ไข:</strong> {reviewImp?.description || '-'}
          </div>
          <Form.Group>
            <Form.Label>ความเห็น (ไม่บังคับ)</Form.Label>
            <Form.Control as="textarea" rows={2} value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              placeholder="ความเห็นเพิ่มเติม..." />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)} disabled={submittingReview}>ยกเลิก</Button>
          <Button variant="danger" onClick={() => handleReview('rejected')} disabled={submittingReview}>
            {submittingReview ? <Spinner size="sm" /> : <><i className="bi bi-x-circle me-1"></i>ปฏิเสธ</>}
          </Button>
          <Button variant="success" onClick={() => handleReview('approved')} disabled={submittingReview}>
            {submittingReview ? <Spinner size="sm" /> : <><i className="bi bi-check-circle me-1"></i>อนุมัติ</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-exclamation-triangle-fill text-danger me-2"></i>ยืนยันการลบ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>คุณต้องการลบแบบประเมินวันที่ <strong>{assessment?.visitDate}</strong> สาขา <strong>{assessment?.branch?.fullName || assessment?.branch?.username}</strong> ใช่หรือไม่?</p>
          <p className="text-danger small mb-0">การลบจะไม่สามารถย้อนกลับได้ และจะลบข้อมูลการแก้ไขทั้งหมดที่เกี่ยวข้องด้วย</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>ยกเลิก</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <Spinner size="sm" /> : <><i className="bi bi-trash me-1"></i>ลบแบบประเมิน</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Improvement submission modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-upload me-2"></i>
            ส่งการแก้ไข: <code>{modalItem?.itemCode}</code>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {improvError && <Alert variant="danger">{improvError}</Alert>}
          <div className="mb-3 text-muted small">{modalItem?.description}</div>
          <Form.Group className="mb-3">
            <Form.Label>รายละเอียดการแก้ไข <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea" rows={3} value={improvDesc}
              onChange={e => setImprovDesc(e.target.value)}
              placeholder="อธิบายสิ่งที่ได้ดำเนินการแก้ไข..."
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>รูปภาพหลักฐาน (jpg, png, webp)</Form.Label>
            <Form.Control
              type="file" multiple accept="image/jpeg,image/png,image/webp"
              onChange={e => setImprovFiles(Array.from(e.target.files))}
            />
            <Form.Text className="text-muted">เลือกได้หลายรูป ขนาดไม่เกิน 10MB ต่อรูป</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submittingImprov}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleSubmitImprovement} disabled={submittingImprov}>
            {submittingImprov ? <><Spinner size="sm" className="me-2" />กำลังส่ง...</> : <><i className="bi bi-send me-2"></i>ส่งการแก้ไข</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default QscDetail;
